import { apiClient } from '@/lib/apiClient';

export type AppointmentStatus =
  | 'PendingConfirmation'
  | 'Confirmed'
  | 'CheckedIn'
  | 'Completed'
  | 'Cancelled'
  | 'NoShow';

export type AppointmentType = 'InPerson' | 'Telemedicine';

export interface CalendarAppointment {
  id: string;
  seatId: string;
  doctorId: string;
  patientId: string;
  patientName: string;
  startAt: string;
  endAt: string;
  status: AppointmentStatus;
  type: AppointmentType;
  noShowRisk: number | null;
  noShowFlagged: boolean;
}

export interface AppointmentDetail {
  id: string;
  status: AppointmentStatus;
  depositAmount: number;
  depositPaidAt: string | null;
  isDepositRefunded: boolean;
  /** Patient no-login deposit link; null when no deposit is due. */
  depositUrl: string | null;
}

/** Full appointment record (includes booking-deposit status + pay link). */
export const getAppointment = async (id: string) => {
  const { data } = await apiClient.get<AppointmentDetail>(`/appointments/${id}`);
  return data;
};

/** Appointments overlapping [from, to) in a cabinet. Times are ISO (UTC). */
export const getCalendar = async (
  cabinetId: string,
  from: string,
  to: string,
  opts?: { seatId?: string; doctorId?: string },
) => {
  const { data } = await apiClient.get<{ data: CalendarAppointment[] }>(`/cabinets/${cabinetId}/calendar`, {
    params: { from, to, seatId: opts?.seatId, doctorId: opts?.doctorId },
  });
  return data.data;
};

/** External-calendar busy blocks (§7.10) for the cabinet's doctors in a window — shown greyed on the calendar. */
export interface ExternalBusyBlock {
  doctorId: string;
  startUtc: string;
  endUtc: string;
}

export const getCalendarBusy = async (cabinetId: string, from: string, to: string, doctorId?: string) => {
  const { data } = await apiClient.get<{ data: ExternalBusyBlock[] }>(`/cabinets/${cabinetId}/calendar/busy`, {
    params: { from, to, doctorId },
  });
  return data.data;
};

/** A patient's appointments (recent first) — for starting a consultation from the patient page. */
export const getPatientAppointments = async (patientId: string) => {
  const { data } = await apiClient.get<{ data: CalendarAppointment[] }>(`/patients/${patientId}/appointments`);
  return data.data;
};

export interface BookAppointmentInput {
  cabinetId: string;
  seatId: string;
  doctorId: string;
  patientId: string;
  startAt: string;
  endAt: string;
  type: AppointmentType;
  notes?: string;
}

export const bookAppointment = async (input: BookAppointmentInput) => {
  const { data } = await apiClient.post('/appointments', input);
  return data;
};

export const confirmAppointment = (id: string) => apiClient.post(`/appointments/${id}/confirm`);
export const checkInAppointment = (id: string) => apiClient.post(`/appointments/${id}/check-in`);
export const cancelAppointment = (id: string, reason?: string) =>
  apiClient.post(`/appointments/${id}/cancel`, { cancelledBy: 'Staff', reason });
export const rescheduleAppointment = (id: string, startAt: string, endAt: string, seatId?: string) =>
  apiClient.post(`/appointments/${id}/reschedule`, { startAt, endAt, seatId });
